import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { cacheClear } from "@/lib/whb-cache";
import { useAccountVisibilityStore } from "./account-visibility-store";

/**
 * Auth store. Supports two session kinds:
 *
 *  - `demo`  — the built-in mock login (SignInPage picks one of three
 *              providers and stuffs a fake user object in). Persisted in
 *              sessionStorage so HMR reloads don't kick the viewer back
 *              to sign-in mid-evaluation.
 *
 *  - `real`  — an authenticated session backed by an HttpOnly `whb_jwt`
 *              cookie on whb-client.pages.dev (set by the /api/session
 *              Pages Function after the WHB bridge hand-off). The raw JWT
 *              is NEVER available to JS; the store only knows there's a
 *              live session and the expiry it was promised.
 *
 * The persist layer only keeps the shape `{ kind, user, expiresAt }`, no
 * tokens. See docs/REAL-LOGIN-RECON.md for the full flow.
 */
export type AuthKind = "demo" | "real";

export interface User {
  name: string;
  email: string;
  provider: "microsoft" | "google" | "facebook" | "demo";
}

interface AuthState {
  kind: AuthKind | null;
  user: User | null;
  signedIn: boolean;
  /** Unix seconds. Only meaningful when kind === "real". */
  expiresAt: number | null;

  /** Mock sign-in — existing demo path. */
  signIn: (user: User) => void;
  /** Real sign-in — called by /auth/callback after /api/session succeeds. */
  signInReal: (user: User | null, expiresAt: number) => void;
  /** Local-only clear. Does NOT call /api/logout or the WHB bridge —
   *  use lib/auth.logoutFullFlow() for that. */
  signOut: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      kind: null,
      user: null,
      signedIn: false,
      expiresAt: null,

      signIn: (user) => set({ kind: "demo", user, signedIn: true, expiresAt: null }),

      signInReal: (user, expiresAt) =>
        set({ kind: "real", user, signedIn: true, expiresAt }),

      signOut: () => {
        set({ kind: null, user: null, signedIn: false, expiresAt: null });
        // Drop every IndexedDB cache entry on sign-out so a different
        // user signing in on the same browser doesn't inherit the
        // previous session's account list / activities / orders.
        // Fire-and-forget — the UI doesn't need to wait for the
        // wipe to complete before navigating to /sign-in.
        void cacheClear();
        // The per-user account hide list is also local-only state;
        // reset it so a different user starts with every account
        // visible. (The account ids wouldn't match their accounts
        // anyway — ids are per-user — but clearing is tidier than
        // carrying orphaned ids.)
        useAccountVisibilityStore.getState().reset();
        // DO NOT clear the following stores on sign-out — they are UI
        // preferences, not user-scoped data, and should survive a
        // sign-out / sign-in cycle (Sergey Apr-2026: "Make sure any
        // cards order and selection will survive re-login and sign
        // out"):
        //   • whb-metrics-settings  — Metrics panel card order + hidden
        //   • whb-chart-settings    — Chart chip/line visibility per mode
        //   • whb-theme             — Dark/light
        //   • whb-ui-version        — V1/V2 toggle
        //   • whb-dev-flags         — Debug toggles
        // If a future requirement surfaces "wipe EVERYTHING on logout",
        // add explicit `.reset()` calls here and document why.
      },
    }),
    {
      name: "whb-auth",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
